import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../../../ApiService";
import { API_ENDPOINTS } from "../../../../ApiService";
import Title from "../../../components/title/Title";
import { MainContainer } from "../../styles/sharedStyles";
import Section from "../../../components/section/Section";
import { tokens } from "../../../../theme";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Paper,
  useMediaQuery,
} from "@mui/material";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

const GrilleDesRecettes = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if it's a mobile screen
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedDietaryPreference, setSelectedDietaryPreference] =
    useState("");
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [selectedType, setSelectedType] = useState(""); // "salée" or "sucrée"

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const response = await apiService.get(API_ENDPOINTS.RECETTE_GET_ALL);
        setRecipes(response.data);
        setFilteredRecipes(response.data);
      } catch (error) {
        console.error("Error fetching recipes:", error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_CATEGORIES
        );
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const fetchDietaryPreferences = async () => {
      try {
        const response = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_DIETARY_PREFERENCES
        );
        setDietaryPreferences(response.data);
      } catch (error) {
        console.error("Error fetching dietary preferences:", error);
      }
    };

    fetchRecipes();
    fetchCategories();
    fetchDietaryPreferences();
  }, []);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    filterRecipes(
      event.target.value,
      selectedCategory,
      selectedDietaryPreference
    );
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    filterRecipes(searchQuery, event.target.value, selectedDietaryPreference);
  };

  const handleDietaryPreferenceChange = (event) => {
    setSelectedDietaryPreference(event.target.value);
    filterRecipes(searchQuery, selectedCategory, event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    filterRecipes(
      searchQuery,
      selectedCategory,
      selectedDietaryPreference,
      event.target.value
    );
  };

  const filterRecipes = (query, category, dietaryPreference, type) => {
    let filtered = recipes;
    if (query) {
      filtered = filtered.filter((recipe) =>
        recipe.nomRecette?.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (category) {
      filtered = filtered.filter((recipe) =>
        (recipe.categories || []).includes(category)
      );
    }
    if (dietaryPreference) {
      filtered = filtered.filter((recipe) =>
        (recipe.dietaryPreferences || []).includes(dietaryPreference)
      );
    }
    if (type) {
      filtered = filtered.filter((recipe) => recipe.typeRecette === type);
    }
    setFilteredRecipes(filtered);
  };

  const handleRecipeClick = (recetteId) => {
    navigate(`/recettes/${recetteId}`);
  };

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Rechercher une recette"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Filtrer par catégorie</InputLabel>
            <Select value={selectedCategory} onChange={handleCategoryChange}>
              <MenuItem value="">Toutes les catégories</MenuItem>
              {categories.map((category) => (
                <MenuItem
                  key={category.recetteCategorieId}
                  value={category.recetteCategorieName}
                >
                  {category.recetteCategorieName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Préférences alimentaires</InputLabel>
            <Select
              value={selectedDietaryPreference}
              onChange={handleDietaryPreferenceChange}
            >
              <MenuItem value="">Toutes les préférences</MenuItem>
              {dietaryPreferences.map((preference) => (
                <MenuItem
                  key={preference.dietaryPreferencesId}
                  value={preference.dietaryPreferenceName}
                >
                  {preference.dietaryPreferenceName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Type de Recette</InputLabel>
            <Select value={selectedType} onChange={handleTypeChange}>
              <MenuItem value="">Toutes</MenuItem>
              <MenuItem value="salée">Salée</MenuItem>
              <MenuItem value="sucrée">Sucrée</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        {filteredRecipes.map((recipe) => (
          <Grid item xs={12} sm={6} md={4} key={recipe.recetteId}>
            <Card
              onClick={() => handleRecipeClick(recipe.recetteId)}
              style={{ cursor: "pointer" }}
            >
              <CardMedia
                component="img"
                height="140"
                image={
                  recipe.picturePath
                    ? recipe.picturePath // ✅ Use full API URL directly
                    : "https://via.placeholder.com/300"
                }
                alt={recipe.nomRecette}
              />

              <CardContent>
                <Typography variant="h6">{recipe.nomRecette}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {recipe.description.substring(0, 60)}...
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginTop: "10px" }}
                >
                  Voir la recette
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default GrilleDesRecettes;
